import React from 'react';
import I18n from 'i18n-js';

import { CoreSiteBannerHero, core_apimessages_ContentSponsorship_SponsorshipType } from '../../rql';

import CoreLink from '../../components/core_link';
import { MParticleEventName } from '../../elog/mparticle_types';
import SearchHeader from '../headers/search_header';
import { useUser } from '../../user_hooks';
import { IUser } from '../../components/user_context_provider';
import { trackEvent } from '../../elog/mparticle_tracker';
import AdTag from '../../components/ads/ad_tag';

const COMPONENT_NAME = 'SiteBannerHero';
const DEFAULT_SEARCH_HEADER = 'SearchHeader';

interface ContentSponsorshipProps {
  contentSponsorship: CoreSiteBannerHero.ContentSponsorship;
}

interface UserProps {
  user: Partial<IUser>;
}

function buildClickEvent(siteBanner: CoreSiteBannerHero.SiteBanner) {
  return {
    eventName: MParticleEventName.SiteBanner,
    componentName: COMPONENT_NAME,
    entityId: siteBanner.id,
    isAd: !!siteBanner.contentSponsorship,
    isDefault: false,
  };
}

function PartnershipSponsorshipContent({ contentSponsorship }: ContentSponsorshipProps) {
  if (
    contentSponsorship?.sponsorshipType === core_apimessages_ContentSponsorship_SponsorshipType.PARTNERSHIP &&
      contentSponsorship?.partnershipName) {
    return (
      <p className="size-80 mb-1 style-italic">
        {I18n.t('commons.contentSponsorship.partnership', { partner: contentSponsorship.partnershipName })}
      </p>
    );
  }

  return null;
}

function AdSponsorshipContent({ contentSponsorship }: ContentSponsorshipProps) {
  if (contentSponsorship?.sponsorshipType === core_apimessages_ContentSponsorship_SponsorshipType.AD) {
    return (
      <div className="site-banner-hero__ad">
        <AdTag inline small />
      </div>
    );
  }

  return null;
}

function DefaultSearchHeader({ user }: UserProps) {
  const searchHeaderSubtitleText = user.loggedOut ? null : I18n.t('commons.searchHeader.loggedInSubtitle');
  return (
    <SearchHeader
      subtitle={searchHeaderSubtitleText}
      trackSearchFn={buildTrackSearchFn()}
      renderSecondaryImage
      rotatingImages
    />
  );
}

function getBackgroundImageStyle(siteBanner: CoreSiteBannerHero.SiteBanner) {
  return { backgroundImage: `url(${siteBanner.imageUrl})` };
}

function buildTrackSearchFn() {
  return function trackSearchFn(query) {
    trackEvent(
      {
        eventName: MParticleEventName.SiteSearch,
        componentName: DEFAULT_SEARCH_HEADER,
        query,
        isDefault: true,
        interactionType: 'fromSearchHeader',
      },
    );
  };
}

export default function SiteBannerHero({ siteBanner }: { siteBanner: CoreSiteBannerHero.SiteBanner }) {
  const user = useUser();

  if (!siteBanner || !siteBanner.hasPhotos) {
    return <DefaultSearchHeader user={user} />;
  }

  const { contentSponsorship } = siteBanner;

  return (
    <div className="site-banner-hero">
      <CoreLink
        to={siteBanner.url.href}
        className="site-banner-hero__inner"
        style={getBackgroundImageStyle(siteBanner)}
        clickEvent={buildClickEvent(siteBanner)}
      >
        <div className="site-banner-hero__content">
          <PartnershipSponsorshipContent contentSponsorship={contentSponsorship} />
          <p className="site-banner-hero__subtext">
            {siteBanner.subheading}
          </p>
          <h2 className="site-banner-hero__headline">
            {siteBanner.heading}
          </h2>
          <span className="site-banner-hero__cta">
            {siteBanner.callToAction}
          </span>
        </div>
        <AdSponsorshipContent contentSponsorship={contentSponsorship} />
      </CoreLink>
    </div>
  );
}
